import { DaysOffButton } from "@/components/work/DaysOff";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAddMember, useMe, useMembers, useRemoveMember, useUpdateMember, useUserOptions } from "@/api/hooks";
import type { ProjectMember } from "@/api/types/ProjectMember";
import type { Role } from "@/api/types/Role";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { useToast } from "@/components/Toast";
import { Avatar, BotTag, Button, Chip, EmptyState, Field, LoadError, Notice, PageSpinner, RoleChip, Select } from "@/components/ui";
import { SettingsTabs } from "@/components/AreaTabs";
import { useProjectContext } from "./ProjectLayout";

export const ROLES: Role[] = ["manager", "developer", "tester", "viewer"];

/** `<option>`s for a project role override: "" means "same as platform role". */
export function RoleOverrideOptions() {
  const { t } = useTranslation();
  return (
    <>
      <option value="">{t("members.sameAsPlatform")}</option>
      {ROLES.map((r) => (
        <option key={r} value={r}>
          {t(`roles.${r}`)}
        </option>
      ))}
    </>
  );
}

export function ProjectMembers() {
  const { t } = useTranslation();
  const project = useProjectContext();
  const members = useMembers(project.key);
  const canManage = project.my_role === "manager";
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ProjectMember | null>(null);

  return (
    <section className="flex flex-col gap-4">
      {canManage ? (
        <SettingsTabs
          project={project}
          actions={
            <Button size="sm" variant="primary" icon="plus" onClick={() => setAdding(true)}>
              {t("members.add")}
            </Button>
          }
        />
      ) : (
        <h2 className="m-0 text-lg font-semibold">{t("members.title")}</h2>
      )}
      {!canManage && <Notice>{t("members.readOnly")}</Notice>}

      {members.isPending ? (
        <PageSpinner />
      ) : members.isError ? (
        <LoadError onRetry={() => void members.refetch()} />
      ) : members.data.length === 0 ? (
        <div className="card">
          <EmptyState icon="users" title={t("members.empty")} />
        </div>
      ) : (
        <div className="card relative overflow-x-auto">
          <table className="table-base min-w-[640px]">
            <thead>
              <tr>
                <th>{t("members.user")}</th>
                <th>{t("members.platformRole")}</th>
                <th>{t("members.projectRole")}</th>
                {canManage && <th className="w-px">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>}
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => (
                <MemberRow key={m.user_id} member={m} canManage={canManage} onRemove={() => setRemoving(m)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="m-0 text-xs text-muted">{t("members.managerAlways")}</p>

      {canManage && (
        <AddMemberDialog
          open={adding}
          onClose={() => setAdding(false)}
          existing={members.data?.map((m) => m.user_id) ?? []}
        />
      )}
      <RemoveMemberDialog member={removing} onClose={() => setRemoving(null)} />
    </section>
  );
}

function MemberRow({ member, canManage, onRemove }: { member: ProjectMember; canManage: boolean; onRemove: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const project = useProjectContext();
  const update = useUpdateMember(project.key);
  const overridable = member.platform_role !== "manager";

  const change = (value: string) =>
    update.mutate(
      { userId: member.user_id, body: { role_override: (value || null) as Role | null } },
      { onSuccess: () => toast.success(t("members.updated")), onError: (e) => toast.error(e) },
    );

  return (
    <tr>
      <td>
        <div className="flex items-center gap-2.5">
          <Avatar name={member.display_name} bot={member.is_bot} size={28} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-medium">
              {member.display_name}
              {member.is_bot && <BotTag />}
              {member.user_id === me.id && <span className="text-muted"> ({t("users.you")})</span>}
            </div>
            <div className="font-mono text-xs text-muted">{member.username}</div>
          </div>
          {member.disabled && <Chip tone="outline">{t("members.disabled")}</Chip>}
        </div>
      </td>
      <td>
        <RoleChip role={member.platform_role} />
      </td>
      <td>
        {canManage && overridable ? (
          <Select
            aria-label={`${t("members.projectRole")}: ${member.display_name}`}
            value={member.role_override ?? ""}
            onChange={(e) => change(e.target.value)}
            disabled={update.isPending}
            className="max-w-[240px]"
          >
            <RoleOverrideOptions />
          </Select>
        ) : (
          <RoleChip role={member.effective_role} />
        )}
      </td>
      {canManage && (
        <td className="whitespace-nowrap">
          {project.scrum_enabled && <DaysOffButton userId={member.user_id} name={member.display_name} />}
          <Button variant="ghost" size="sm" onClick={onRemove}>
            {t("common.remove")}
          </Button>
        </td>
      )}
    </tr>
  );
}

function AddMemberDialog({ open, onClose, existing }: { open: boolean; onClose: () => void; existing: string[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const project = useProjectContext();
  const options = useUserOptions(open);
  const add = useAddMember(project.key);
  const [userId, setUserId] = useState("");
  const [override, setOverride] = useState("");

  const available = useMemo(
    () => (options.data ?? []).filter((u) => !existing.includes(u.id)),
    [options.data, existing],
  );
  const selected = available.find((u) => u.id === userId);

  const close = () => {
    setUserId("");
    setOverride("");
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    add.mutate(
      { user_id: userId, role_override: (override || null) as Role | null },
      {
        onSuccess: () => {
          toast.success(t("members.added"));
          close();
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t("members.add")}
      width={460}
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button type="submit" form="add-member" variant="primary" loading={add.isPending} disabled={!userId}>
            {t("members.add")}
          </Button>
        </>
      }
    >
      {options.isPending ? (
        <PageSpinner />
      ) : options.isError ? (
        <LoadError onRetry={() => void options.refetch()} />
      ) : available.length === 0 ? (
        <p className="m-0 text-muted">{t("members.noUsersLeft")}</p>
      ) : (
        <form id="add-member" onSubmit={submit} className="flex flex-col gap-4">
          <Field label={t("members.user")}>
            {(p) => (
              <Select {...p} value={userId} onChange={(e) => setUserId(e.target.value)} autoFocus required>
                <option value="" disabled>
                  {t("members.pickUser")}
                </option>
                {available.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name} ({u.username}){u.is_bot && ` · ${t("common.bot")}`} · {t(`roles.${u.role}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {selected && selected.role !== "manager" && (
            <Field label={t("members.projectRole")}>
              {(p) => (
                <Select {...p} value={override} onChange={(e) => setOverride(e.target.value)}>
                  <RoleOverrideOptions />
                </Select>
              )}
            </Field>
          )}
          {selected?.role === "manager" && <p className="m-0 text-xs text-muted">{t("members.managerAlways")}</p>}
        </form>
      )}
    </Dialog>
  );
}

function RemoveMemberDialog({ member, onClose }: { member: ProjectMember | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const project = useProjectContext();
  const remove = useRemoveMember(project.key);

  return (
    <ConfirmDialog
      open={!!member}
      onClose={onClose}
      danger
      loading={remove.isPending}
      title={t("members.removeTitle", { name: member?.display_name ?? "", project: project.name })}
      body={t("members.removeBody")}
      confirmLabel={t("common.remove")}
      onConfirm={() =>
        member &&
        remove.mutate(member.user_id, {
          onSuccess: () => {
            toast.success(t("members.removed"));
            onClose();
          },
          onError: (e) => toast.error(e),
        })
      }
    />
  );
}
